import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from './firebase';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EnvironmentManager from '../config/EnvironmentManager';
import { 
  Home, Bell, User, Settings, LogOut,
  Upload, BookOpen, Archive, BarChart3,
  Plus, Users, Award, Clock, Search,
  Play, Star, ChevronRight, Filter,
  Download, Edit, Trash2, Eye,
  Loader, IndianRupee 
} from 'lucide-react';
import './ManageCourses.css';

const ManageCoursesPage = () => {
  const auth = getAuth(app);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await fetchVideoCourses(currentUser.uid);
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchVideoCourses = async (userId) => {
    try {
      setLoading(true);
      const idToken = await getIdToken();
      
      if (!idToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.getVideoCourseDetailsByUser}/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setCourses(response.data || []);

    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getIdToken = async () => {
    const user = getAuth(app).currentUser;
    return user ? await user.getIdToken(true) : null;
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleEditCourse = (course) => {
    navigate('/MHMV-profile/upload-video-course', { 
      state: { 
        editMode: true, 
        courseData: course 
      } 
    });
  };

  const handleViewCourse = (course) => {
    navigate('/MHMV-profile/video-courses', { 
      state: { selectedCourse: course } 
    });
  };

  const handleArchiveCourse = async (course) => {
    if (!course || !user) return;

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        setError('Authentication required');
        return;
      }

      const response = await axios.delete(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.archiveVideoCourse}/${course.courseId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.includes('archived successfully')) {
        // Refresh the courses list
        await fetchVideoCourses(user.uid);
        setError('Course archived successfully');
        setTimeout(() => setError(''), 3000);
      }

    } catch (error) {
      console.error('Archive error:', error);
      setError('Failed to archive course. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteCourse = async (course) => {
    if (!course || !user) return;
    
    if (!window.confirm(`Are you sure you want to delete "${course.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        setError('Authentication required');
        return;
      }

      const response = await axios.delete(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.deleteVideoCourseDetails}/${course.courseId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.includes('deleted successfully')) {
        // Refresh the courses list
        await fetchVideoCourses(user.uid);
        setError('Course deleted successfully');
        setTimeout(() => setError(''), 3000);
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError('Failed to delete course. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Calculate user stats from actual courses
  const userStats = {
    totalCourses: courses.length,
    enrolledStudents: courses.reduce((total, course) => total + (course.enrolledCount || 0), 0),
    totalRevenue: courses.reduce((total, course) => total + (course.price || 0), 0),
    averageRating: courses.length > 0 
      ? (courses.reduce((total, course) => total + (course.rating || 0), 0) / courses.length).toFixed(1)
      : 0
  };

  const quickActions = [
    {
      title: 'Upload New Course',
      description: 'Create and upload a new video course',
      icon: Upload,
      path: '/MHMV-profile/upload-course-excel',
      color: '#3B82F6'
    },
    {
      title: 'View All Courses',
      description: 'Manage and edit your existing courses',
      icon: BookOpen,
      path: '/MHMV-profile/video-courses',
      color: '#10B981'
    },
    {
      title: 'Archived Courses',
      description: 'View and restore archived courses',
      icon: Archive,
      path: '/MHMV-profile/archived-courses',
      color: '#F59E0B'
    },
    {
      title: 'Course Analytics',
      description: 'View performance and student insights',
      icon: BarChart3,
      path: '/MHMV-profile/course-analytics',
      color: '#8B5CF6'
    }
  ];

  const filters = [
    { id: 'all', label: 'All Courses' },
    { id: 'published', label: 'Published' },
    { id: 'draft', label: 'Draft' },
    { id: 'archived', label: 'Archived' }
  ];

  // Filter courses based on active filter and search
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.category?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = activeFilter === 'all' || 
                         (activeFilter === 'archived' && course.archiveCourse) ||
                         (activeFilter === 'published' && !course.archiveCourse) ||
                         (activeFilter === 'draft' && course.draft); // Assuming you have a draft field

    return matchesSearch && matchesFilter;
  });

  // Helper functions (same as in your video courses view)
  const getTotalVideosCount = (course) => {
    let count = 0;
    course.modules?.forEach(module => {
      module.submodules?.forEach(submodule => {
        submodule.coursevideo?.forEach(courseVideo => {
          count += courseVideo.video?.length || 0;
        });
      });
    });
    return count;
  };

  const getTotalDuration = (course) => {
    const totalVideos = getTotalVideosCount(course);
    const totalMinutes = totalVideos * 30;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="manage-courses-container">
        <header className="manage-header">
          <div className="manage-header-content">
            <div className="manage-header-left">
              <button 
                className="manage-home-btn"
                onClick={() => handleNavigation('/MHMV-profile/video-course-home')}
              >
                <Home size={20} />
                <span>Home</span>
              </button>
            </div>
            
            <div className="manage-header-center">
              <h1 className="manage-page-title">Manage Your Courses</h1>
              <p className="manage-page-subtitle">Create, organize, and track all your video courses in one place</p>
            </div>
          </div>
        </header>

        <div className="manage-loading-state">
          <Loader size={48} className="manage-loading-spinner" />
          <p>Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-courses-container">
      {/* Header */}
      <header className="manage-header">
        <div className="manage-header-content">
          <div className="manage-header-left">
  <div className="manage-home-dropdown">
    <button 
      className="manage-home-btn"
      onClick={() => handleNavigation('/MHMV-profile/video-course-home')}
      onMouseEnter={() => setShowStudentDropdown(true)}
      onMouseLeave={() => setShowStudentDropdown(false)}
    >
      <Home size={20} />
      <span>Home</span>
    </button>
    
    {showStudentDropdown && (
      <div 
        className="manage-home-dropdown-menu"
        onMouseEnter={() => setShowStudentDropdown(true)}
        onMouseLeave={() => setShowStudentDropdown(false)}
      >
        <div className="manage-home-dropdown-item">
          <div className="manage-home-dropdown-text">
            <span className="manage-home-dropdown-title">
              Switch to the student view here
            </span>
            <span className="manage-home-dropdown-subtitle">
              Get back to the courses you're taking
            </span>
          </div>
        </div>
      </div>
    )}
  </div>
</div>
          
          

          <div className="manage-header-right">
            <button className="manage-notification-btn">
              <Bell size={20} />
              <span className="manage-notification-badge">3</span>
            </button>
            
            <div className="manage-user-dropdown">
              <button 
                className="manage-user-btn"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <div className="manage-user-avatar">
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <span className="manage-user-name">
                  {user?.displayName || user?.email?.split('@')[0] || 'User'}
                </span>
              </button>

              {showUserDropdown && (
                <div className="manage-dropdown-menu">
                  <button className="manage-dropdown-item">
                    <User size={16} />
                    <span>My Profile</span>
                  </button>
                  <button className="manage-dropdown-item">
                    <Settings size={16} />
                    <span>Settings</span>
                  </button>
                  <div className="manage-dropdown-divider"></div>
                  <button 
                    className="manage-dropdown-item manage-dropdown-logout"
                    onClick={() => auth.signOut()}
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="manage-main-content">
        {/* Error Message */}
        {error && (
          <div className={`manage-message ${error.includes('successfully') ? 'manage-message-success' : 'manage-message-error'}`}>
            {error}
          </div>
        )}

        {/* Welcome Section */}
        <section className="manage-welcome-section">
          <div className="manage-welcome-content">
            <h2 className="manage-welcome-title">
              Welcome back, {user?.displayName || user?.email?.split('@')[0] || 'Instructor'}
            </h2>
            <p className="manage-welcome-subtitle">
              Manage your courses, track performance, and grow your audience.
            </p>
          </div>
          
          <div className="manage-stats-grid">
            <div className="manage-stat-card">
              <div className="manage-stat-icon" style={{ backgroundColor: '#EFF6FF' }}>
                <BookOpen size={24} color="#3B82F6" />
              </div>
              <div className="manage-stat-info">
                <div className="manage-stat-number">{userStats.totalCourses}</div>
                <div className="manage-stat-label">Total Courses</div>
              </div>
            </div>
            
            <div className="manage-stat-card">
              <div className="manage-stat-icon" style={{ backgroundColor: '#F0FDF4' }}>
                <Users size={24} color="#10B981" />
              </div>
              <div className="manage-stat-info">
                <div className="manage-stat-number">{userStats.enrolledStudents}</div>
                <div className="manage-stat-label">Students</div>
              </div>
            </div>
            
            
            
            
          </div>
        </section>

        {/* Quick Actions Section */}
        <section className="manage-actions-section">
          <h3 className="manage-section-title">Quick Actions</h3>
          <div className="manage-actions-grid">
            {quickActions.map((action, index) => (
              <div 
                key={index}
                className="manage-action-card"
                onClick={() => handleNavigation(action.path)}
              >
                <div 
                  className="manage-action-icon"
                  style={{ backgroundColor: action.color }}
                >
                  <action.icon size={24} color="white" />
                </div>
                <div className="manage-action-content">
                  <h4 className="manage-action-title">{action.title}</h4>
                  <p className="manage-action-description">{action.description}</p>
                </div>
                <div className="manage-action-arrow">
                  <ChevronRight size={20} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Courses Management Section */}
        <section className="manage-courses-section">
          <div className="manage-section-header">
            <div className="manage-section-title-group">
              <h3 className="manage-section-title">Your Courses</h3>
              <p className="manage-section-subtitle">Manage and organize your course library</p>
            </div>
            
            <div className="manage-courses-controls">
              <div className="manage-search-box">
                <Search size={18} className="manage-search-icon" />
                <input
                  type="text"
                  placeholder="Search your courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="manage-search-input"
                />
              </div>
              
              <div className="manage-filter-tabs">
                {filters.map(filter => (
                  <button
                    key={filter.id}
                    className={`manage-filter-tab ${activeFilter === filter.id ? 'active' : ''}`}
                    onClick={() => setActiveFilter(filter.id)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Courses Table */}
          <div className="manage-courses-table">
            <div className="manage-table-header">
              <div className="manage-table-col course-col">Course</div>
              
              <div className="manage-table-col duration-col">Duration</div>
             
              <div className="manage-table-col status-col">Status</div>
              <div className="manage-table-col actions-col">Actions</div>
            </div>
            
            <div className="manage-table-body">
              {filteredCourses.length > 0 ? (
                filteredCourses.map((course) => (
                  <div key={course.courseId} className="manage-table-row">
                    <div className="manage-table-col course-col">
                      <div className="manage-course-info">
                        <h4 className="manage-course-title">{course.title || 'Untitled Course'}</h4>
                        <div className="manage-course-meta">
                          <span className="manage-course-category">{course.category || 'General'}</span>
                          
                          <span className="manage-course-price">
  {course.price > 0 ? (
    <>
      <IndianRupee size={12} />
      {course.price}
    </>
  ) : 'FREE'}
</span>
                        </div>
                      </div>
                    </div>
                    
                    
                    
                    <div className="manage-table-col duration-col">
                      <div className="manage-duration-info">
                        <Clock size={16} />
                        <span>{getTotalDuration(course)}</span>
                      </div>
                    </div>
                    
                    
                    
                    <div className="manage-table-col status-col">
                      <span className={`manage-status-badge ${course.archiveCourse ? 'archived' : 'published'}`}>
                        {course.archiveCourse ? 'Archived' : 'Published'}
                      </span>
                    </div>
                    
                    <div className="manage-table-col actions-col">
                      <div className="manage-action-buttons">
                        <button 
                          className="manage-action-btn view-btn" 
                          title="View Course"
                          onClick={() => handleViewCourse(course)}
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          className="manage-action-btn edit-btn" 
                          title="Edit Course"
                          onClick={() => handleEditCourse(course)}
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          className="manage-action-btn archive-btn" 
                          title={course.archiveCourse ? 'Unarchive Course' : 'Archive Course'}
                          onClick={() => handleArchiveCourse(course)}
                        >
                          <Archive size={16} />
                        </button>
                        <button 
                          className="manage-action-btn delete-btn" 
                          title="Delete Course"
                          onClick={() => handleDeleteCourse(course)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="manage-empty-state">
                  <BookOpen size={48} />
                  <h3>No courses found</h3>
                  <p>
                    {searchQuery || activeFilter !== 'all' 
                      ? 'Try adjusting your search or filters'
                      : 'You haven\'t created any courses yet'
                    }
                  </p>
                  {!searchQuery && activeFilter === 'all' && (
                    <button 
                      onClick={() => handleNavigation('/MHMV-profile/upload-video-course')}
                      className="manage-upload-cta"
                    >
                      <Upload size={16} />
                      Upload Your First Course
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Mobile App Banner */}
        
      </main>

      {/* Footer */}
      <footer className="manage-footer">
        <div className="manage-footer-content">
          <div className="manage-footer-main">
            <div className="manage-footer-brand">
              <BookOpen size={24} />
              <span className="manage-footer-brand-text">MHMV Learning Platform</span>
            </div>
            <p className="manage-footer-description">
              A Faster Way For Your Employees To Grow And Upskill
            </p>
          </div>
          
          <div className="manage-footer-links">
            <div className="manage-footer-column">
              <h4>Platform</h4>
              <a href="#">Upload Course</a>
              <a href="#">Manage Courses</a>
              <a href="#">View Analytics</a>
              <a href="#">Student Management</a>
            </div>
            
            <div className="manage-footer-column">
              <h4>Support</h4>
              <a href="#">Help Center</a>
              <a href="#">Instructor Resources</a>
              <a href="#">Contact Support</a>
              <a href="#">Community</a>
            </div>
            
            <div className="manage-footer-column">
              <h4>Company</h4>
              <a href="#">About Us</a>
              <a href="#">Careers</a>
              <a href="#">Blog</a>
              <a href="#">Partners</a>
            </div>
          </div>
        </div>
        
        <div className="manage-footer-bottom">
          <p>&copy; 2025 MHMV Learning Platform. All rights reserved.</p>
          <div className="manage-footer-legal">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
            <a href="#">Cookie Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ManageCoursesPage;